import { useCallback } from 'react';
import { useWorkspaceError } from '@/contexts/WorkspaceErrorContext';
import { useToast } from '@/components/common/Toast/ToastContainer';
import { ApiError } from '@/utils/errors/apiErrorWrapper';
import { createCustomerFacingMessage } from '@/utils/errors/extractApiError';
import { parseAzureError } from '@/utils/errors/parseAzureError';

/**
 * Custom hook for handling workspace errors with automatic Azure error parsing
 * and toast notifications.
 * 
 * @param workspaceId - Optional workspace ID to associate errors with
 * @returns Object with error handling functions
 */
export const useWorkspaceErrorHandler = (workspaceId?: string) => {
  const { addError } = useWorkspaceError();
  const { showError, showSuccess, showWarning, showInfo } = useToast();

  /**
   * Handles an error by:
   * 1. Extracting comprehensive error information if it's an ApiError
   * 2. Otherwise extracting from error message, stack, or response
   * 3. Parsing Azure errors from all available sources
   * 4. Adding to workspace error context with full details
   * 5. Showing customer-facing toast notification
   */
  const handleError = useCallback(
    (
      error: Error | ApiError,
      context?: string,
      response?: unknown,
      showToast = true
    ) => {
      let extractedError: {
        error: Error;
        responseBody?: unknown;
        responseText?: string;
        azureError?: ReturnType<typeof parseAzureError>;
      };
      let customerMessage: string | undefined;

      // If it's already an ApiError, use its extracted information
      if (error instanceof ApiError) {
        extractedError = error.extractedError;
        customerMessage = error.customerMessage;
      } else {
        // Otherwise, extract error information synchronously where possible
        extractedError = {
          error: error instanceof Error ? error : new Error(String(error)),
          responseBody: response,
        };
        
        // Try to extract more info if response is available
        if (response) {
          try {
            const responseStr = typeof response === 'string' 
              ? response 
              : JSON.stringify(response);
            // Parse Azure errors from response
            const azureError = parseAzureError(responseStr);
            if (azureError.isAzureError) {
              extractedError.azureError = azureError;
              extractedError.responseText = responseStr;
              customerMessage = createCustomerFacingMessage({
                error: extractedError.error,
                responseBody: response,
                responseText: responseStr,
                azureError,
              });
            }
          } catch {
            // If parsing fails, continue with basic error
          }
        }
        
        // Also check error message and stack for Azure errors
        const azureErrorFromMessage = parseAzureError(error);
        if (azureErrorFromMessage.isAzureError && !extractedError.azureError) {
          extractedError.azureError = azureErrorFromMessage;
          customerMessage = createCustomerFacingMessage({
            error: extractedError.error,
            responseBody: response,
            azureError: azureErrorFromMessage,
          });
        }
      }

      // Create error with context if provided
      const errorWithContext = context
        ? new Error(`${context}: ${extractedError.error.message}`)
        : extractedError.error;

      // Add error to workspace error context with full extracted information
      // Pass the full extracted error as response for maximum detail
      addError(
        errorWithContext,
        undefined,
        workspaceId,
        extractedError.responseBody || extractedError.responseText || response
      );

      // Show toast notification with customer-facing message
      if (showToast) {
        const message = customerMessage || extractedError.error.message;
        showError(context ? `${context}: ${message}` : message);
      }
    },
    [addError, workspaceId, showError]
  );

  /**
   * Handles a success case with toast notification
   */
  const handleSuccess = useCallback(
    (message: string, duration?: number) => {
      showSuccess(message, duration);
    },
    [showSuccess]
  );

  /**
   * Handles a warning case with toast notification
   */
  const handleWarning = useCallback(
    (message: string, duration?: number) => {
      showWarning(message, duration);
    },
    [showWarning]
  );

  /**
   * Handles an info case with toast notification
   */
  const handleInfo = useCallback(
    (message: string, duration?: number) => {
      showInfo(message, duration);
    },
    [showInfo]
  );

  return {
    handleError,
    handleSuccess,
    handleWarning,
    handleInfo,
  };
};
